import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RangeService } from '../range.service';

@Component({
  selector: 'ps-ui-range',
  templateUrl: './range.component.html',
  styleUrls: ['./range.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RangeComponent,
      multi: true,
    },
  ],
})
export class RangeComponent implements OnInit, ControlValueAccessor, OnChanges {
  finalValue: number = 50;
  @Input() value?: number;
  @Input() min: number = 0;
  @Input() max: number = 100;
  @Input() step?: number;
  @Input() currency: boolean = false;
  @Input() symbol: string = '%';
  @Input() disabled: boolean = false;
  @Input() formatter?: (value: number) => string;
  @Output() valueChange = new EventEmitter<number>();

  onChange?: (arg: number) => void;

  constructor(private _service: RangeService) {}

  get percent(): number {
    return this._service.percent(this.min, this.max, this.value);
  }
  get valueLabel(): string {
    return this._format(this._valueOrDefault);
  }
  get minLabel(): string {
    return this._format(this.min);
  }
  get maxLabel(): string {
    return this._format(this.max);
  }

  budgetProgressRangeStyle: string = '--webkitProgressPercent: 50%';

  ngOnInit() {
    const { symbol, step } = this._service.init(
      this.min,
      this.max,
      this.currency,
      this.symbol,
      this.step
    );
    this.symbol = symbol;
    this.step = step;
    this._initValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const value = changes.value?.currentValue;
    if (!value) {
      this._updateRangeUpiutProgressBar(this._valueOrDefault);
    } else {
      this._updateRangeUpiutProgressBar(value);
    }
  }

  private _updateRangeUpiutProgressBar(value: number) {
    const leftPercentage = this.calculateRangeInputProgressPercentage(
      value,
      this.min,
      this.max
    );
    // Workaround for chrome-based browsers
    this.budgetProgressRangeStyle = `--webkitProgressPercent: ${Math.round(
      leftPercentage
    )}%`;
  }

  private get _valueOrDefault() {
    return this._service.valueOrDefault(this.min, this.max, this.value);
  }

  private _initValue() {
    // set default value in the middle if it's not passed to component
    if (!this.value) {
      this.value = this._valueOrDefault;
      this._updateRangeUpiutProgressBar(this.value);
    } else {
      this._updateRangeUpiutProgressBar(this.value);
    }
  }

  updateValue(value: number) {
    this.value = value;
    this._updateRangeUpiutProgressBar(value);
    if (this.onChange) {
      this.onChange(value);
    }
    this.valueChange.emit(value);
  }

  calculateRangeInputProgressPercentage = (
    currentIndex: number,
    min: number,
    max: number
  ) => {
    return Number(((currentIndex - min) * 100) / (max - min));
  };

  private _format(value: number): string {
    if (this.formatter) {
      return this.formatter(value);
    }
    return this._service.format(value, this.currency, this.symbol);
  }

  writeValue(value: number) {
    this.value = value;
  }
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched() {
    // do nothing. Needed for ControlValueAccessor interface
  }
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
